/* eslint-disable no-console */
import React, {
    useState,
    useEffect,
    useContext,
    useCallback
} from 'react';
import _ from 'lodash';
import appConfig from 'app-config';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { platformMessages } from 'ml-components-platform-react';
import PolChangeTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PolChangeTrackEvent.jsx';
import { MLEndorsementUtil, MLEndorsementFlowUtil, MLLocalDateUtil } from 'ml-portals-util-js';
import { MLPropertyValuationService, MLMultiLobsDataHolderService, MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { MLHOPropertyValuationComponent, MLHOReplacementCostSummaryComponent } from 'ml-components-common-react';
import messages from './PropertyDetailsPolChangePage.messages';
import { Identify  } from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/TrackEvent';

import metadata from './PropertyDetailsPolChangePage.metadata.json5';
import styles from './PropertyDetailsPolChangePage.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';

// MLHOPropertyDetailsCtrl.js, ml-ho-property-details.html
function PropertyDetailsPolChangePage(props) {
    const isGPA = appConfig.portalCode === 'GPA';

    const { authHeader } = useAuthentication();
    const { EndorsementService } = useDependencies('EndorsementService');

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { wizardData, updateWizardData, currentStep, goNext, steps, currentStepIndex, changeNextSteps } = props;
    const [showPropertyValuation, updateShowPropertyValuation] = useState(false);
    const [showAdditionalQuestions, updateShowAdditionalQuestions] = useState(false);
    const [replacementCost, setReplacementCost] = useState(false);
    const [editHomeDetails, setEditHomeDetails] = useState(undefined);
    const [closeCovAAlert, updateCloseCovAAlert] = useState(false);
    const [disableNextButton, setDisableNextButton] = useState(false);
    const [showErrors, updateShowErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const {
        onValidate,
        isComponentValid
    } = useValidation('PropertyDetailsPolChangePage');
    const USER_TYPE = isGPA ? 'AGENT' : 'CONSUMER';
    const CONSTRUCTION_VERISK_DETAILS_PATH = 'lobData.homeowners.coverables.constructionVeriskDetails_ML.value';
    const [token, setToken] = useState();

    const { submissionVM: endorsementVM } = wizardData;
    const isCondo = endorsementVM.value.isCondo();
    const hasPreviousReplacementCost = !!_.get(endorsementVM, 'lobData.homeowners.coverables.constructionDetails_ML.replacementCost.value');
    const coverageType = _.get(endorsementVM, 'lobData.homeowners.coverables.coveragePartType_ML.value.code', undefined);
    const {
        showProgressDialog,
        showErrorModal
    } = MLModalService();

    const generateVeriskToken = useCallback(() => {
        const request = {
            quoteID: endorsementVM.jobID.value,
            userType: USER_TYPE
        };
        const promise = MLPropertyValuationService.getPropertyValuationInfoMLByJobNumber(request, authHeader);
        showProgressDialog(platformMessages.retrievingHomeDetails, promise);
        promise.then((data) => {
            setToken(data.tokenId);
            updateShowPropertyValuation(true);
        });
        promise.catch((error) => {
            console.error(error);
        });
    }, [endorsementVM.jobID.value, USER_TYPE, isGPA, authHeader, showProgressDialog]);

    const onInit = useCallback((editHomeDetailsClicked = false) => {
        setReplacementCost(false);
        const tempEditHomeDetails = editHomeDetailsClicked || editHomeDetails;
        if (!hasPreviousReplacementCost || !!tempEditHomeDetails) {
            generateVeriskToken();
            setEditHomeDetails(false);
        } else {
            updateShowAdditionalQuestions(true);
        }
    }, [editHomeDetails, generateVeriskToken, hasPreviousReplacementCost]);

    useEffect(() => {
        Identify('Endorsement Step Reached', {step: 'Property Details Screen'}, endorsementVM, authHeader)
    }, [])
    
    useEffect(() => {
        if (coverageType !== 'hoprental') {
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.updatedPropertyDetails), false, currentStep.mlStepName, authHeader);
            onInit();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isGPA) {
            PolChangeTrackEvent('Endorsement Step Reached', false, endorsementVM, authHeader, { step: 'PolChange Property Details Screen' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);

    const writeValue = useCallback((value, path) => {
        const newEndorsementVM = viewModelService.clone(endorsementVM);
        _.set(newEndorsementVM, path, value);
        updateWizardSubmissionData(newEndorsementVM);
    }, [endorsementVM, updateWizardSubmissionData, viewModelService]);

    const updateValuationData = useCallback((valuationId, version) => {
        const veriskDetails = _.get(endorsementVM, CONSTRUCTION_VERISK_DETAILS_PATH);
        veriskDetails.verisk360Version_ML = version;
        veriskDetails.verisk360ValuationID_ML = valuationId;
        veriskDetails.updateDwellingWithVeriskData = true;
    }, [endorsementVM]);

    const initDwellingDateModifiers = useCallback(() => {
        const date = new Date();
        const dateModifiers = ['zk9gmhjs1bsr143asmo0m9nmlga', 'z50j8jrtptqs14rv13ms7vbppt8', 'zipj8i6jij6ulbada49qcopeota'];
        const modifiers = _.get(endorsementVM, 'lobData.homeowners.modifiers_ML');
        modifiers.dwellingsModifiers_ML.forEach((mod) => {
            if (dateModifiers.includes(mod.patternCode.value)) {
                _.set(mod, 'chosenTermValue.value', date);
                _.set(mod, 'dateValue_ML.value', MLLocalDateUtil.date2LocalDate(date));
            }
        });
    }, [endorsementVM]);

    const showAdditionalQuestionsSection = () => {
        updateShowAdditionalQuestions(true);
        updateShowPropertyValuation(false);
    };

    const onFinishVeriskIntegration = useCallback((valuationId, version) => {
        showAdditionalQuestionsSection();
        updateValuationData(valuationId, version);
        const promise = EndorsementService.saveEndorsement([endorsementVM.value], authHeader);
        showProgressDialog(messages.savingEndorsementDetails, promise);
        promise.then((endorsement) => {
            const policyChange = new PolicyChange(Object.assign({}, endorsement));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            _.set(updatedEndorsementVM, 'lobData.homeowners.coverables.constructionDetails_ML.savedFromPortal.value', true);
            _.set(wizardData, 'submissionVM', updatedEndorsementVM);
            updateWizardSubmissionData(updatedEndorsementVM);
            initDwellingDateModifiers();
            setReplacementCost(true);
        });
        promise.catch((error) => {
            const title = messages.unableToSaveEndorsement;
            const message = messages.errorWhenSavingEndorsement;
            console.error(error);
            showErrorModal(title, message);
        });
    }, [EndorsementService, authHeader, endorsementVM.value, initDwellingDateModifiers, showErrorModal, showProgressDialog, updateValuationData, updateWizardSubmissionData, viewModelService, wizardData]);

    const handleVeriskError = () => {
        const title = messages.somethingWentWrong;
        const description = messages.pleaseTryAgain;
        showErrorModal(title, description);
    };

    const onClickEditHomeDetailsButton = useCallback(() => {
        if (!isComponentValid) {
            updateShowErrors(true);
            setErrorTimestamp(errorTimestamp+1);
        } else {
            updateShowAdditionalQuestions(false);
            setEditHomeDetails(true);
            updateCloseCovAAlert(true);
            onInit(true);
        }
    }, [errorTimestamp, isComponentValid, onInit]);

    const onNext = useCallback(async () => {
        if (!isComponentValid || !(endorsementVM.aspects.valid && endorsementVM.aspects.subtreeValid)) {
            updateShowErrors(true);
            setTimeout(()=>{
                setErrorTimestamp(errorTimestamp+1);
            }, 500);

            return;
        }

        if (isGPA) {
            PolChangeTrackEvent('Endorsement Step Submitted', false, endorsementVM, authHeader, {
                step: 'PolChange Property Details Screen',
                action: 'Property Details Submitted'
            });
        }
        
        const lbl = MLEndorsementUtil.stepLabel(currentStep);
        MLEndorsementUtil.setEndorsementDescription(lbl, endorsementVM, translator);
        MLNavigationStepsTrackingService.completeCurrentStep(null, authHeader);
        MLTagManagerService.sendFormStepPolicyChangeStep();

        const productCode = _.get(endorsementVM, 'baseData.productCode.value', '');
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep('endorsementAdditionalChanges', steps, currentStepIndex, changeNextSteps, goNext, productCode);
    }, [currentStep, endorsementVM, translator, authHeader, steps, currentStepIndex, changeNextSteps, goNext, isComponentValid, errorTimestamp]);

    const resolvers = {
        resolveComponentMap: {
            propertyValuationComponent: MLHOPropertyValuationComponent,
            replacementCostSummaryComponent: MLHOReplacementCostSummaryComponent
        },
        resolveCallbackMap: {
            onNext
        }
    };

    const nextStepText = () => {
        return (
            <FormattedMessage
                {...messages.nextLabel}
                values={{
                    additionalChangesAreNeeded: (
                        <strong>{translator(messages.additionalChangesAreNeeded)}</strong>
                    )
                }}
            />
        );
    };

    const overrides = {
        contentContainer: {
            className: isGPA ? 'page-content-gpa' : ''
        },
        ampSubTitle: {
            visible: !isGPA
        },
        propertyDetailsHeader: {
            content: translator(messages.propertyDetails),
            visible: isGPA
        },
        propertyValuationComponent: {
            visible: showPropertyValuation,
            token,
            onFinish: onFinishVeriskIntegration,
            onError: handleVeriskError
        },
        replacementCostSummaryComponent: {
            visible: showAdditionalQuestions,
            isCondo,
            accountName: endorsementVM.baseData.accountName.value,
            onValueChange: writeValue,
            dwellingConstructionModifiersML: endorsementVM.lobData.homeowners.modifiers_ML.dwellingConstructionModifiers_ML,
            isPolicyChange: true,
            replacementCost,
            submissionVM: endorsementVM,
            onClickEditHomeDetailsButton,
            showErrors: showErrors,
            onValidate,
            closeCovAAlert,
            updateWizardData: updateWizardSubmissionData,
            setDisableNextButton
        },
        stickyContinueSection: {
            visible: isGPA,
            defaultContinueSectionId: 'footer',
            nextDescription: nextStepText(),
            onNext
        },
        footer: {
            visible: showAdditionalQuestions
        },
        nextDescription: {
            content: nextStepText()
        }
    };

    const skipOnResumeIf = () => {
        if (!!_.get(endorsementVM, 'bindData.policyNumber.value')
            && (endorsementVM.value.baseData.status_ML === 'Bound' || endorsementVM.value.baseData.status_ML === 'Binding')) {
            return true;
        }

        if (MLMultiLobsDataHolderService.getLastVisitedStep() === currentStep.mlStepName) {
            return false;
        }

        if (coverageType === 'hoprental') {
            return true;
        }

        if (MLMultiLobsDataHolderService.getLastVisitedStep() === 'quoteDetails' &&
            endorsementVM.hvqQuoteID_ML.value &&
            endorsementVM.isBundle_ML.value) {
            return true;
        }

        if (endorsementVM.value.isBundle_ML
            && endorsementVM.value.baseData.status_ML !== 'Draft') {
            return true;
        }

        const savedFromPortal = endorsementVM.lobData.homeowners.coverables.constructionDetails_ML.savedFromPortal.value;
        const constructionReplacementCost = !!_.get(endorsementVM, 'lobData.homeowners.coverables.constructionDetails_ML.replacementCost.value');
        return (savedFromPortal && constructionReplacementCost);
    };

    return (
        <WizardPage
            policyChangePageHeaderML={isGPA ? messages.makeChanges : messages.propertyDetails}
            showCancel={false}
            showPrevious={false}
            showNext={false}
            showErrors={showErrors}
            skipWhen={skipOnResumeIf}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={endorsementVM}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
                showErrors={showErrors}
                callbackMap={resolvers.resolveCallbackMap}
                resolveClassNameMap={styles}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
}

PropertyDetailsPolChangePage.propTypes = wizardProps;
export default PropertyDetailsPolChangePage;